<template>
  <div class="app-container">
    <!-- 顶部导航栏 -->
    <div class="nav-bar">
      <div class="nav-left" @click="goback">
        <van-icon name="arrow-left" size="20" class="back-icon" />
        <span>返回</span>
      </div>
      <div class="nav-title">{{ form.Id === '' ? '新增' : '编辑' }}委托单</div>
      <div class="nav-right"></div>
    </div>

    <!-- ================= 委托方基础信息 ================= -->
    <div class="card">
      <div class="card-title">
        <van-icon name="manager-o" class="title-icon" />
        <span>基础信息</span>
      </div>

      <van-cell-group :border="false">
        <van-field v-model="form.OrderNo" readonly label-width="110" placeholder="选择寄样日期后自动生成" class="order-no-field">
          <template #label
            >委托编号</template
          >
        </van-field>

        <van-field v-model="form.CompanyName" readonly label-width="110" placeholder="自动获取">
          <template #label
            >委托单位<span class="required-mark">*</span></template
          >
        </van-field>

        <van-field v-model="form.CustomerNo" label-width="110" placeholder="请输入客户编号（选填）" :readonly="isApproved">
          <template #label
            >客户编号</template
          >
        </van-field>

        <van-cell
          title-width="110"
          :value="formatDateDisplay(form.SendDate)"
          :is-link="!isApproved"
          @click.stop.prevent="openSampleCalendar"
        >
          <template #title
            >寄样日期<span class="required-mark">*</span></template
          >
        </van-cell>

        <van-cell
          title-width="110"
          :value="formatDateDisplay(form.ExpectedDate)"
          :is-link="!isApproved"
          @click.stop.prevent="openExpectCalendar"
        >
          <template #title
            >完成日期</template
          >
          <template #label>
            <span class="cell-tip">以深海确认日期为准</span>
          </template>
        </van-cell>

        <van-field v-model="form.ProjectAddress" label-width="110" placeholder="请输入委托地址/项目名称/地址" :readonly="isApproved">
          <template #label
            >委托地址<span class="required-mark">*</span></template
          >
        </van-field>

        <van-field v-model="form.ContactPhone" type="tel" label-width="110" placeholder="请输入联系人手机号" :readonly="isApproved">
          <template #label
            >联系人手机号<span class="required-mark">*</span></template
          >
        </van-field>

        <van-field label-width="110">
          <template #label
            >报告输出语言<span class="required-mark">*</span></template
          >
          <template #input>
            <van-radio-group v-model="form.OutputLanguage" direction="horizontal" class="radio-inline" :disabled="isApproved">
              <van-radio name="1">中文版</van-radio>
              <van-radio name="2">中英文对照</van-radio>
            </van-radio-group>
          </template>
        </van-field>
      </van-cell-group>
    </div>

    <!-- ================= 样品信息 ================= -->
    <div class="card sample-section-card">
      <div class="card-title">
        <van-icon name="label-o" class="title-icon" />
        <span>样品信息</span>
      </div>

      <van-field label-width="0" class="method-field">
        <template #input>
          <van-radio-group v-model="form.SampleInputMethod" direction="horizontal" class="method-radio" :disabled="isApproved">
            <van-radio name="1">自行填写样品信息</van-radio>
            <van-radio name="2">上传样品附件</van-radio>
          </van-radio-group>
        </template>
      </van-field>

      <div v-if="form.SampleInputMethod === '2'" class="attach-upload">
        <input
          ref="fileInput"
          type="file"
          accept=".xls,.xlsx,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style="display: none"
          @change="handleAttachment"
        />

        <van-button size="small" type="info" plain icon="down" @click="downAttachmentTemplate">
          下载附件模板
        </van-button>

        <van-button v-if="!isApproved" size="small" plain type="info" icon="plus" @click="$refs.fileInput.click()">
          上传附件
        </van-button>
        <span v-if="form.AttachmentFile" class="file-name">
          <van-icon name="description" />
          {{ form.AttachmentFile.name }}
          <span v-if="!isApproved" class="remove-file" @click="form.AttachmentFile = null">×</span>
        </span>
        <span v-else-if="!isApproved" class="attach-tip"
          >请优先下载附件模板，在模板内完成样品信息填写后上传附件，附件仅支持 .xls / .xlsx / .doc / .docx，单个文件</span
        >

        <van-button v-if="form.Id !== ''" size="small" type="info" plain icon="eye-o" @click="viewAttachment">
          查看样品附件
        </van-button>
      </div>

      <template v-if="form.SampleInputMethod === '1'">
        <div v-for="(sample, idx) in form.Samples" :key="idx" class="sample-item">
          <div class="sample-item-title">
            <span class="sample-item-index">样品 {{ idx + 1 }}</span>
            <span v-if="form.Samples.length > 1 && !isApproved" class="del-btn" @click.stop.prevent="removeSample(idx)">删除本条样品</span>
          </div>

          <div v-if="!isApproved" class="sample-name-tags">
            <span
              v-for="tag in sampleNameTags"
              :key="tag"
              class="sample-name-tag"
              :class="{ active: sample.sampleName === tag }"
              @click.stop.prevent="selectSampleNameTag(idx, tag)"
              >{{ tag }}</span
            >
          </div>

          <van-field
            v-model="sample.sampleName"
            label-width="110"
            placeholder="请输入或上方及右侧选择"
            class="inline-field"
            :readonly="isApproved"
            @blur="rememberSampleName(sample.sampleName)"
          >
            <template #label
              >试样名称<span class="required-mark">*</span></template
            >
            <template #button>
              <van-icon v-if="!isApproved" name="arrow-down" class="dropdown-icon" @click.stop.prevent="openSampleNamePicker(idx)" />
            </template>
          </van-field>

          <div class="field-block">
            <div class="field-label-row">
              <div class="field-label">样品描述</div>
              <span v-if="!isApproved" class="add-desc-btn" @click="addDescription(idx)">+ 添加一条样品描述</span>
            </div>

            <div v-for="(desc, di) in sample.descriptions" :key="di" class="desc-row">
              <input v-model="desc.category" class="desc-input desc-category" type="text" placeholder="分类" :readonly="isApproved" />
              <span class="desc-sep">：</span>
              <input
                v-model="desc.content"
                class="desc-input desc-content"
                type="text"
                placeholder="请输入具体内容"
                :readonly="isApproved"
              />
              <van-icon
                v-if="!isApproved && sample.descriptions.length > 1"
                name="cross"
                class="desc-del"
                @click.stop.prevent="removeDescription(idx, di)"
              />
            </div>
          </div>

          <div class="field-block">
            <div class="field-label-row">
              <div class="field-label">牌号/厚度<span class="required-mark">*</span></div>
            </div>
            <div class="spec-row">
              <input v-model="sample.mark" class="desc-input spec-mark" type="text" placeholder="请输入牌号" :readonly="isApproved" />
              <span class="desc-sep">/</span>
              <div class="spec-thickness-box">
                <input
                  v-model="sample.thickness"
                  class="desc-input spec-thickness"
                  type="text"
                  placeholder="请输入厚度"
                  :readonly="isApproved"
                />
              </div>
            </div>
          </div>

          <van-field
            v-model="sample.quantity"
            type="text"
            inputmode="numeric"
            :formatter="onlyInteger"
            format-trigger="onChange"
            label-width="110"
            placeholder="请输入数量"
            class="inline-field"
            :readonly="isApproved"
          >
            <template #label
              >来样数量<span class="required-mark">*</span></template
            >
          </van-field>

          <div class="field-block">
            <div class="field-label-row">
              <div class="field-label">检测项目<span class="required-mark">*</span></div>
              <span v-if="!isApproved" class="add-desc-btn" @click="addTestItem(idx)">+ 添加一条检测项目</span>
            </div>

            <div v-for="(ti, tidx) in sample.testItems" :key="tidx" class="test-item-block">
              <div v-if="!isApproved" class="test-item-header">
                <span class="reuse-item-btn" @click.stop.prevent="reuseTestItem(idx, tidx)">复用本条</span>
                <van-icon
                  v-if="sample.testItems.length > 1"
                  name="cross"
                  class="desc-del test-item-del"
                  @click.stop.prevent="removeTestItem(idx, tidx)"
                />
              </div>

              <!-- 检测项目：标签单独一行 -->
              <div class="test-item-row test-item-row-stacked">
                <div class="test-item-label test-item-label-stacked">检测项目</div>
                <div class="input-with-arrow">
                  <input
                    v-model="ti.itemName"
                    class="desc-input"
                    type="text"
                    placeholder="请输入或点击右侧选择检测项目"
                    :readonly="isApproved"
                  />
                  <van-icon
                    v-if="!isApproved"
                    name="arrow-down"
                    class="test-item-arrow"
                    @click.stop.prevent="openTestItemNamePicker(idx, tidx)"
                  />
                </div>
              </div>

              <!-- 检测数量：标签与输入框同一行（保持原样） -->
              <div class="test-item-row test-item-row-stacked">
                <div class="test-item-label test-item-label-stacked">检测数量</div>
                <input
                  :value="ti.quantity"
                  class="desc-input test-item-qty"
                  type="text"
                  inputmode="numeric"
                  placeholder="请输入检测数量"
                  :readonly="isApproved"
                  @input="onIntegerInput($event, ti, 'quantity')"
                />
              </div>

              <!-- 试验依据标准：标签单独一行 + CMA/CNAS 绿色高亮 -->
              <div class="test-item-row test-item-row-stacked">
                <div class="test-item-label test-item-label-stacked">试验依据标准</div>
                <div class="input-with-arrow standard-input-container">
                  <input
                    v-model="ti.testStandard"
                    class="desc-input"
                    :class="{ 'standard-input-hidden-text': hasCmaOrCnas(ti.testStandard) }"
                    type="text"
                    placeholder="可输入或点击右侧选择"
                    :readonly="isApproved"
                  />
                  <div
                    v-if="hasCmaOrCnas(ti.testStandard)"
                    class="standard-highlight-mirror"
                    v-html="highlightStandardText(ti.testStandard)"
                  ></div>
                  <van-icon
                    v-if="!isApproved"
                    name="arrow-down"
                    class="test-item-arrow"
                    @click.stop.prevent="openTestStandardPicker(idx, tidx)"
                  />
                </div>
              </div>

              <div v-if="isStandardOutOfScope(idx, tidx)" class="standard-warning">
                手动输入的标准：{{ ti.testStandard }} 需由深海工作人员核实是否出具CMA / CNAS资质证书!若核实后不在深海 CMA / CNAS
                认可范围内，报告将无法加盖检测资质章（CMA / CNAS 章），仅可出具未盖章测试结果。
              </div>

              <!-- 判定标准：标签单独一行 -->
              <div class="test-item-row test-item-row-stacked">
                <div class="test-item-label test-item-label-stacked">判定标准</div>
                <input
                  v-model="ti.judgmentStandard"
                  class="desc-input test-item-qty"
                  type="text"
                  placeholder="请输入判定标准"
                  :readonly="isApproved"
                />
              </div>

              <!-- 备注：标签单独一行 -->
              <div class="test-item-row test-item-row-stacked">
                <div class="test-item-label test-item-label-stacked">备注</div>
                <input
                  v-model="ti.remark"
                  class="desc-input test-item-qty"
                  type="text"
                  placeholder="请输入检测具体要求"
                  :readonly="isApproved"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="!isApproved" class="sample-buttons">
          <van-button block plain type="info" @click="reuseLastSample">复用上一条样品</van-button>
          <van-button block type="info" @click="addSample">+ 添加样品</van-button>
        </div>
      </template>
    </div>

    <!-- ================= 样品寄送方式 ================= -->
    <div class="card" ref="shippingCardRef" :class="{ 'shipping-card-highlight': highlightShipping }">
      <div class="card-title">
        <van-icon name="logistics" class="title-icon" />
        <span>样品寄送方式</span>
      </div>

      <van-field label-width="110">
        <template #label
          >寄送方式</template
        >
        <template #input>
          <van-radio-group v-model="form.ShippingMethod" direction="horizontal" :disabled="isApproved" class="radio-inline">
            <van-radio name="1">自己邮寄</van-radio>
            <van-radio name="2">深海邮寄</van-radio>
          </van-radio-group>
        </template>
      </van-field>

      <template v-if="form.ShippingMethod === '1'">
        <van-field label-width="110">
          <template #label
            >快递公司</template
          >
          <template #input>
            <van-radio-group v-model="form.ExpressCompany" direction="horizontal" :disabled="isApproved" class="radio-inline">
              <van-radio name="1">顺丰快递</van-radio>
              <van-radio name="2">京东快递</van-radio>
            </van-radio-group>
          </template>
        </van-field>

        <van-field v-model="form.ExpressNo" label-width="110" :disabled="isApproved" placeholder="请输入快递单号">
          <template #label
            >快递单号</template
          >
        </van-field>
      </template>
    </div>

    <!-- ================= 项目其他补充说明 ================= -->
    <div class="card">
      <div class="card-title">
        <van-icon name="edit" class="title-icon" />
        <span>补充说明</span>
      </div>
      <van-field
        v-model="form.Remarks"
        type="textarea"
        rows="3"
        autosize
        :border="false"
        placeholder="请补充特殊试验要求、加急、整改需求等"
        class="remark-textarea"
        :readonly="isApproved"
      />
    </div>

    <!-- ================= 签字确认 ================= -->
    <div class="card sign-card">
      <div class="card-title">
        <van-icon name="records" class="title-icon" />
        <span>签字确认</span>
      </div>

      <div
        class="sign-section"
        :class="{
          'sign-section--disabled': !isApproved,
          'sign-section--locked': isApproved
        }"
      >
        <canvas
          ref="signatureCanvas"
          class="sign-canvas-inline"
          @touchstart.prevent="startSign"
          @touchmove.prevent="moveSign"
          @touchend="endSign"
          @mousedown="startSign"
          @mousemove="moveSign"
          @mouseup="endSign"
          @mouseleave="endSign"
        ></canvas>

        <img
          v-if="form.SignatureData && !String(form.SignatureData).startsWith('data:')"
          :src="form.SignatureData"
          class="sign-image-inline"
          alt="签名"
        />
        <div v-else-if="!form.SignatureData && !isSigning && !isApproved" class="sign-placeholder">请在此处手写签字</div>
        <div v-if="form.SignatureData" class="sign-signed-badge"><van-icon name="passed" /> 已签字</div>
      </div>

      <div class="sign-info-row">
        <span class="sign-date">签字日期：{{ form.SignatureDate || '—' }}</span>
        <div class="sign-actions">
          <van-button v-if="form.SignatureData && !isApproved" size="small" plain type="warning" @click="reSign">
            重新签字
          </van-button>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div v-if="!isApproved" class="submit-btn-wrap">
      <van-button class="draft-btn" plain type="info" :disabled="isOrderNoEmpty()" @click="saveDraft(false)">暂存</van-button>
      <van-button class="submit-btn" type="info" :disabled="isOrderNoEmpty()" @click="submitForm()">提交委托单</van-button>
    </div>

    <!-- 检测项目名称选择弹窗 -->
    <van-popup v-model="testItemNamePicker.show" position="bottom" round class="test-popup">
      <div class="popup-title">
        <van-icon name="apps-o" />
        <span>选择检测项目</span>
      </div>

      <input v-model="testItemNamePicker.search" class="popup-search" placeholder="搜索常用检测项目" />

      <div class="test-options-scroll">
        <div v-for="item in filteredTestItemNames" :key="item" class="test-option" @click="onTestItemNameSelect(item)">
          <span class="check" :class="{ done: isCurrentTestItemName(item) }"></span>
          <span>{{ item }}</span>
        </div>
        <div v-if="!filteredTestItemNames.length" class="empty-tip">未找到匹配项，可手动输入</div>
      </div>

      <div class="popup-actions">
        <van-button block plain @click="testItemNamePicker.show = false">取消</van-button>
      </div>
    </van-popup>

    <!-- 试验依据标准选择弹窗 -->
    <van-popup v-model="testStandardPicker.show" position="bottom" round class="test-popup">
      <div class="popup-title">
        <van-icon name="records" />
        <span>选择试验依据标准</span>
      </div>

      <input v-model="testStandardPicker.search" class="popup-search" placeholder="搜索常用试验依据标准" />

      <div class="test-options-scroll">
        <div v-for="s in filteredTestStandards" :key="s" class="test-option" @click="onTestStandardSelect(s)">
          <span class="check" :class="{ done: isCurrentTestStandard(s) }"></span>
          <span>{{ s }}</span>
        </div>
        <div v-if="!filteredTestStandards.length" class="empty-tip">未找到匹配项，可手动输入</div>
      </div>

      <div class="popup-actions">
        <van-button block plain @click="testStandardPicker.show = false">取消</van-button>
      </div>
    </van-popup>

    <!-- 试样名称选择弹窗 -->
    <van-popup v-model="sampleNamePicker.show" position="bottom" round class="test-popup">
      <div class="popup-title">
        <van-icon name="label-o" />
        <span>选择试样名称</span>
      </div>

      <div class="popup-input-row">
        <input
          v-model="sampleNamePicker.customInput"
          class="popup-custom-input"
          placeholder="手动输入试样名称，回车添加"
          @keyup.enter="addCustomSampleName"
        />
        <van-button size="small" type="info" plain @click="addCustomSampleName">添加</van-button>
      </div>

      <input v-model="sampleNamePicker.search" class="popup-search" placeholder="搜索常用试样名称" />

      <div class="test-options-scroll">
        <div v-for="name in filteredSampleNames" :key="name" class="test-option" @click="onSampleNameSelect(name)">
          <span class="check" :class="{ done: isCurrentSampleName(name) }"></span>
          <span>{{ name }}</span>
        </div>
        <div v-if="!filteredSampleNames.length" class="empty-tip">未找到匹配项，可手动输入</div>
      </div>

      <div class="popup-actions">
        <van-button block plain @click="sampleNamePicker.show = false">取消</van-button>
      </div>
    </van-popup>

    <!-- 日历 -->
    <van-calendar v-model="showSampleCalendar" :min-date="minDate" @confirm="onSampleDateConfirm" />
    <van-calendar v-model="showExpectCalendar" :min-date="expectMinDate" @confirm="onExpectDateConfirm" />
  </div>
</template>

<script>
import Vue from 'vue'
import { Cell, CellGroup, Calendar, Field, Icon, Radio, RadioGroup, Button, Popup, Checkbox, Toast, Dialog } from 'vant'

Vue.use(Cell)
Vue.use(CellGroup)
Vue.use(Calendar)
Vue.use(Field)
Vue.use(Icon)
Vue.use(Radio)
Vue.use(RadioGroup)
Vue.use(Button)
Vue.use(Popup)
Vue.use(Checkbox)
Vue.use(Toast)
Vue.use(Dialog)

const EXPECT_MIN_GAP_DAYS = 3
// 支持上传的附件扩展名（Excel + Word）
const ALLOWED_ATTACHMENT_EXTS = ['xls', 'xlsx', 'doc', 'docx']
import axios from 'axios'
import moment from 'moment'

export default {
  name: 'addOrders',
  data() {
    return {
      minDate: new Date(2026, 0, 1),
      _lastInitSig: '',
      userId: '',
      showSampleCalendar: false,
      showExpectCalendar: false,
      expectMinGapDays: EXPECT_MIN_GAP_DAYS,
      highlightShipping: false,
      // 试样名称快捷标签
      sampleNameTags: ['钢板', '管材', '板材', '棒材', '焊接试板', '焊接工艺评定试板'],
      form: {
        Id: '',
        CreateUserId: '',
        OrderNo: '',
        CompanyName: '',
        CompanyId: '',
        CustomerNo: '',
        SendDate: '',
        ExpectedDate: '',
        ProjectAddress: '',
        OutputLanguage: '1',
        ContactPhone: '',
        SampleInputMethod: '',
        Samples: [],
        AttachmentFile: null,
        ShippingMethod: '',
        ExpressCompany: '',
        ExpressNo: '',
        Remarks: '',
        RemarkTags: [],
        SignatureData: '',
        SignatureFilePath: '',
        SignatureDate: '',
        CurStatus: ''
      },
      testDictionary: [], // 接口返回的原始检测项目字典
      itemIdMap: {}, // { 检测项目名称: 检测项目ID }
      itemStandardMap: {}, // { 检测项目名称: [试验依据标准名称...] }
      standardIdMap: {}, // { "检测项目名称::标准名称": 标准ID }
      presetTestItems: [], // 检测项目名称列表
      savedSampleNames: [], // 试样名称
      savedTestStandards: [], // 当前检测项目对应的可选试验依据标准（动态刷新）
      testItemNamePicker: { show: false, sampleIdx: -1, itemIdx: -1, search: '', customInput: '' },
      testStandardPicker: { show: false, sampleIdx: -1, itemIdx: -1, search: '', customInput: '' },
      sampleNamePicker: { show: false, currentSampleIndex: -1, search: '', customInput: '' },
      isSigning: false,
      signCtx: null,
      signLast: { x: 0, y: 0 }
    }
  },
  computed: {
    filteredTestItemNames() {
      const s = (this.testItemNamePicker.search || '').trim().toLowerCase()
      if (!s) return this.presetTestItems
      return this.presetTestItems.filter(i => i.toLowerCase().includes(s))
    },
    filteredSampleNames() {
      const s = (this.sampleNamePicker.search || '').trim().toLowerCase()
      if (!s) return this.savedSampleNames
      return this.savedSampleNames.filter(i => i.toLowerCase().includes(s))
    },
    filteredTestStandards() {
      const s = (this.testStandardPicker.search || '').trim().toLowerCase()
      if (!s) return this.savedTestStandards
      return this.savedTestStandards.filter(i => i.toLowerCase().includes(s))
    },
    expectMinDate() {
      const base = this.form.SendDate ? this.parseDate(this.form.SendDate) : new Date()
      base.setHours(0, 0, 0, 0)
      base.setDate(base.getDate() + EXPECT_MIN_GAP_DAYS)
      return base
    },
    expectMinDateStr() {
      return this.form.SendDate ? this.addDays(this.form.SendDate, EXPECT_MIN_GAP_DAYS) : ''
    },
    isApproved() {
      const s = this.form.CurStatus
      return s === '审核通过' || s === '待审核'
    },
    isAuditFinished() {
      const s = this.form.CurStatus
      return s === '审核通过'
    },
    attachmentUrl() {
      const f = this.form || {}
      const candidates = [
        f.AttachmentFilePath,
        f.AttachmentFileUrl,
        f.AttachmentUrl,
        typeof f.AttachmentFile === 'string' ? f.AttachmentFile : ''
      ]
      let url = candidates.find(v => v && String(v).trim()) || ''
      if (!url) return ''
      url = String(url).trim()
      if (/^(https?:|data:|blob:)/i.test(url)) return url
      return window.config.apiUrl + '/' + url.replace(/^\/+/, '')
    }
  },
  watch: {
    'form.SampleInputMethod'(val) {
      if (val === '2') {
        this.form.AttachmentFile = null
      } else {
        if (!this.form.Samples.length) this.addSample()
      }
    },
    'form.ShippingMethod'(val) {
      if (val !== '1') {
        this.form.ExpressCompany = ''
        this.form.ExpressNo = ''
      } else if (!this.form.ExpressCompany) {
        this.form.ExpressCompany = '1'
      }
      this.checkHighlightOff()
    },
    'form.ExpressCompany'() {
      this.checkHighlightOff()
    },
    'form.ExpressNo'() {
      this.checkHighlightOff()
    }
  },
  created() {
    this.initPage()
  },
  activated() {
    this.initPage()
    this.$nextTick(() => {
      setTimeout(() => this.initInlineSignature(), 200)
    })
  },
  deactivated() {
    this._lastInitSig = ''
  },
  beforeRouteUpdate(to, from, next) {
    next()
    this.$nextTick(() => {
      this.initPage()
      setTimeout(() => this.initInlineSignature(), 200)
    })
  },
  beforeRouteLeave(to, from, next) {
    this._lastInitSig = ''
    next()
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize)
    window.addEventListener('orientationchange', this.onWindowResize)
    document.addEventListener('focusin', this.onGlobalFocusIn, true)
    document.addEventListener('blur', this.onAnyInputBlur, true)
    this.$nextTick(() => {
      setTimeout(() => this.initInlineSignature(), 150)
    })
  },
  beforeDestroy() {
    this._lastInitSig = ''
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('orientationchange', this.onWindowResize)
    document.removeEventListener('focusin', this.onGlobalFocusIn, true)
    document.removeEventListener('blur', this.onAnyInputBlur, true)
  },
  methods: {
    /* ================= 附件辅助方法 ================= */
    getFileExt(fileName) {
      const n = String(fileName || '').toLowerCase()
      const dotIdx = n.lastIndexOf('.')
      return dotIdx > -1 ? n.substring(dotIdx + 1) : ''
    },
    getFileName(fileName) {
      const n = String(fileName || '').toLowerCase()
      const dotIdx = n.lastIndexOf('.')
      return dotIdx > -1 ? n.substring(0, dotIdx) : ''
    },
    isAllowedAttachment(fileName) {
      const ext = this.getFileExt(fileName)
      return ALLOWED_ATTACHMENT_EXTS.indexOf(ext) > -1
    },

    /* ================= 整数输入辅助方法 ================= */
    onlyInteger(val) {
      if (val === null || val === undefined) return ''
      return String(val).replace(/\D/g, '')
    },
    onIntegerInput(e, obj, key) {
      const cleaned = String((e && e.target && e.target.value) || '').replace(/\D/g, '')
      if (obj) obj[key] = cleaned
      if (e && e.target && e.target.value !== cleaned) {
        e.target.value = cleaned
      }
    },

    /* ================= 试验依据标准 CMA/CNAS 高亮 ================= */
    hasCmaOrCnas(text) {
      return /cma|cnas/i.test(String(text || ''))
    },
    highlightStandardText(text) {
      const s = String(text || '')
      if (!s) return ''
      // 转义 HTML 防止 XSS
      const esc = s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // 将 CMA / CNAS（不区分大小写）包裹在绿色 span 中
      return esc.replace(/(CNAS|CMA)/gi, '<span class="cma-green">$1</span>')
    },

    /* ================= 试样名称快捷标签 ================= */
    selectSampleNameTag(sampleIdx, tag) {
      if (this.isApproved) return
      const sample = this.form.Samples[sampleIdx]
      if (!sample) return
      const nextVal = sample.sampleName === tag ? '' : tag
      this.$set(sample, 'sampleName', nextVal)
      if (nextVal) this.rememberSampleName(nextVal)
    },

    goback() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/index')
      }
    },
    initPage() {
      const q = this.$route.query
      const sig = `${q.userId || ''}_${q.orderId || ''}`
      if (sig === this._lastInitSig) return
      this._lastInitSig = sig

      this.userId = q.userId || ''
      this.resetForm()
      this.form.Id = q.orderId || ''

      if (this.form.Id === '') {
        console.log('11111111111111')
        this.getUserBasicInfo()
        console.log('22222222222222')
        this.addSample()
        console.log('33333333333333')
        this.getDicts()
        console.log('44444444444444')
      } else {
        this.getOrderDetail()
        this.getDicts()
        this.getTestDictionary()
      }
    },
    resetForm() {
      this.form = {
        Id: '',
        CreateUserId: '',
        OrderNo: '',
        CompanyName: '',
        CompanyId: '',
        CustomerNo: '',
        SendDate: '',
        ExpectedDate: '',
        ProjectAddress: '',
        OutputLanguage: '1',
        ContactPhone: '',
        SampleInputMethod: '',
        Samples: [],
        AttachmentFile: null,
        ShippingMethod: '',
        ExpressCompany: '',
        ExpressNo: '',
        Remarks: '',
        RemarkTags: [],
        SignatureData: '',
        SignatureFilePath: '',
        SignatureDate: '',
        CurStatus: ''
      }
      this.isSigning = false
      this.signCtx = null
      this.signLast = { x: 0, y: 0 }
      this.highlightShipping = false
    },
    formatDateDisplay(val) {
      if (!val) return ''
      return String(val).split(/[T ]/)[0]
    },
    onTermsChange(val) {
      if (!val) {
        this.clearSignature()
      } else {
        this.$nextTick(() => {
          setTimeout(() => this.initInlineSignature(), 50)
        })
      }
    },
    clearSignature() {
      const canvas = this.$refs.signatureCanvas
      if (canvas && this.signCtx) {
        const w = canvas.offsetWidth
        const h = canvas.offsetHeight
        this.signCtx.clearRect(0, 0, w, h)
      }
      this.form.SignatureData = ''
      this.form.SignatureFilePath = ''
      this.form.SignatureDate = ''
      this.isSigning = false
    },
    blurActiveElement() {
      const el = document.activeElement
      if (!el || el === document.body || typeof el.blur !== 'function') return
      el.blur()
    },
    onGlobalFocusIn(e) {
      const el = e.target
      if (!el || el.tagName !== 'INPUT') return
      if (el.readOnly || el.type === 'radio' || el.type === 'checkbox') {
        setTimeout(() => {
          try {
            el.blur()
          } catch (err) {}
        }, 0)
      }
    },
    onAnyInputBlur(e) {
      const el = e.target
      if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return
      if (el.readOnly || el.disabled) return
      if (el.closest && el.closest('.van-popup')) return
      const y = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
      if (y <= 0) return
      const restore = () => {
        const cur = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
        if (Math.abs(cur - y) > 40) window.scrollTo(0, y)
      }
      requestAnimationFrame(() => {
        restore()
        setTimeout(restore, 80)
        setTimeout(restore, 200)
      })
    },
    async getUserBasicInfo() {
      const _that = this
      const postLoading = Toast.loading({ duration: 0, forbidClick: true, message: '加载中...' })
      axios
        .get(window.config.apiUrl + '/api/Public/GetUserInfoByOpenId', { params: { userId: _that.userId } })
        .then(function(response) {
          postLoading.clear()
          if (response.data.code == 1) {
            _that.form.CompanyName = response.data.data.CompanyName
            _that.form.ProjectAddress = response.data.data.ProjectAddress
            _that.form.ContactPhone = response.data.data.ContactPhone
            _that.form.CompanyId = response.data.data.CompanyId
            _that.form.CreateUserId = response.data.data.UserId
            // 初始化检测项目字典
            _that.getTestDictionary()
          } else {
            Toast(response.data.msg)
          }
        })
        .catch(function(error) {
          postLoading.clear()
          Toast('网络错误')
        })
    },
    getOrderDetail() {
      const _that = this
      const postLoading = Toast.loading({ duration: 0, forbidClick: true, message: '加载中...' })
      axios
        .get(window.config.apiUrl + '/api/Public/GetCustomerCommissionOrder', { params: { id: _that.form.Id } })
        .then(function(response) {
          postLoading.clear()
          if (response.data.code == 1) {
            const data = response.data.data || {}
            _that.form = data
            if (_that.form.SignatureFilePath) _that.form.SignatureFilePath = window.config.apiUrl + '/' + _that.form.SignatureFilePath
            if (_that.form.CustomerNo == null) _that.form.CustomerNo = ''
            if (_that.form.SignatureFilePath) {
              _that.form.SignatureData = _that.form.SignatureFilePath
            } else {
              _that.form.SignatureData = ''
            }
            if (_that.form.SignatureDate == null) _that.form.SignatureDate = ''
            // _that.normalizeSamples()
            _that.$nextTick(() => {
              _that.initInlineSignature()
              _that.checkShippingAfterAudit()
            })
          } else {
            Toast(response.data.msg)
          }
        })
        .catch(function(error) {
          postLoading.clear()
          Toast('网络错误')
        })
    },
    normalizeSamples() {
      return
      if (!Array.isArray(this.form.Samples)) this.form.Samples = []
      this.form.Samples.forEach(s => {
        if (s.mark == null && s.thickness == null && s.specification) {
          const parts = String(s.specification).split('/')
          const markPart = (parts[0] || '').trim()
          const thicknessPart = (parts[1] || '').replace(/[^\d]/g, '').trim()
          this.$set(s, 'mark', markPart)
          this.$set(s, 'thickness', thicknessPart)
        }
        if (s.mark == null) this.$set(s, 'mark', '')
        if (s.thickness == null) {
          this.$set(s, 'thickness', '')
        }
        delete s.specification

        if (s.quantity != null) {
          const q = String(s.quantity).replace(/\D/g, '')
          this.$set(s, 'quantity', q)
        }

        if (!Array.isArray(s.descriptions) || !s.descriptions.length) {
          s.descriptions = [{ category: '', content: s.description || '' }]
        } else {
          s.descriptions = s.descriptions.map(d => ({
            category: d && d.category != null ? d.category : '',
            content: d && d.content != null ? d.content : ''
          }))
        }
        const oldStd = s.testStandard || ''
        const oldJud = s.judgmentStandard || ''
        if (!Array.isArray(s.testItems) || !s.testItems.length) {
          s.testItems = [
            { itemId: '', itemName: '', quantity: '', testStandard: oldStd, testStandardId: '', judgmentStandard: oldJud, remark: '' }
          ]
        } else {
          s.testItems = s.testItems.map(t => {
            if (typeof t === 'string') {
              return {
                itemId: '',
                itemName: t,
                quantity: '',
                testStandard: oldStd,
                testStandardId: '',
                judgmentStandard: oldJud,
                remark: ''
              }
            }
            return {
              itemId: t && t.itemId != null ? t.itemId : '',
              itemName: t && t.itemName != null ? t.itemName : '',
              quantity: t && t.quantity != null ? String(t.quantity).replace(/\D/g, '') : '',
              testStandard: t && t.testStandard != null ? t.testStandard : oldStd,
              testStandardId: t && t.testStandardId != null ? t.testStandardId : '',
              judgmentStandard: t && t.judgmentStandard != null ? t.judgmentStandard : oldJud,
              remark: t && t.remark != null ? t.remark : ''
            }
          })
        }
      })
    },
    checkShippingAfterAudit() {
      if (!this.isAuditFinished) return
      const f = this.form
      const noShipping = !f.ShippingMethod
      const selfShipInfoMissing = f.ShippingMethod === '1' && (!f.ExpressCompany || !f.ExpressNo || !String(f.ExpressNo).trim())
      if (!noShipping && !selfShipInfoMissing) return
      this.highlightShipping = true
      setTimeout(() => {
        const el = this.$refs.shippingCardRef
        if (el) {
          const dom = el.$el || el
          try {
            dom.scrollIntoView({ behavior: 'smooth', block: 'start' })
          } catch (e) {
            const top = dom.getBoundingClientRect().top + window.pageYOffset - 60
            window.scrollTo(0, top)
          }
        }
        Toast({ message: '委托单审核已通过，请维护样品寄送方式！', duration: 4000, position: 'middle' })
      }, 400)
    },
    checkHighlightOff() {
      if (!this.highlightShipping) return
      const f = this.form
      const selfShipOk = f.ShippingMethod === '1' && f.ExpressCompany && f.ExpressNo && String(f.ExpressNo).trim()
      const deepSeaOk = f.ShippingMethod === '2'
      if (selfShipOk || deepSeaOk) this.highlightShipping = false
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    parseDate(dateStr) {
      if (!dateStr) return new Date()
      const datePart = String(dateStr).split(/[T ]/)[0]
      const [y, m, d] = datePart.split('-').map(Number)
      if (!y || !m || !d || isNaN(y) || isNaN(m) || isNaN(d)) {
        console.warn('parseDate 解析失败:', dateStr)
        return new Date()
      }
      return new Date(y, m - 1, d)
    },
    addDays(dateStr, days) {
      const d = this.parseDate(dateStr)
      d.setDate(d.getDate() + days)
      return this.formatDate(d)
    },
    openSampleCalendar() {
      if (this.isApproved) return
      this.blurActiveElement()
      this.showSampleCalendar = true
    },
    onSampleDateConfirm(date) {
      this.form.SendDate = this.formatDate(date)
      this.showSampleCalendar = false
      const _that = this
      const postLoading = Toast.loading({ duration: 0, forbidClick: true, message: '委托编号获取中...' })
      const loadingTimer = setTimeout(() => postLoading.clear(), 8000)
      axios
        .get(window.config.apiUrl + '/api/Public/GetOrderNo', {
          params: { companyId: _that.form.CompanyId, jiyangDate: _that.form.SendDate },
          timeout: 10000
        })
        .then(function(response) {
          clearTimeout(loadingTimer)
          postLoading.clear()
          if (response.data.code == 1) _that.form.OrderNo = response.data.data.OrderNo
          else Toast(response.data.msg)
        })
        .catch(function(error) {
          clearTimeout(loadingTimer)
          postLoading.clear()
        })
      if (this.form.ExpectedDate) {
        const min = this.addDays(this.form.SendDate, EXPECT_MIN_GAP_DAYS)
        if (this.form.ExpectedDate < min) {
          this.form.ExpectedDate = ''
          Toast(`完成日期已重置，请重新选择（不早于 ${min}）`)
        }
      }
    },
    openExpectCalendar() {
      if (this.isApproved) return
      if (!this.form.SendDate) {
        Toast('请先选择寄样日期')
        return
      }
      this.blurActiveElement()
      this.showExpectCalendar = true
    },
    onExpectDateConfirm(date) {
      const val = this.formatDate(date)
      const min = this.addDays(this.form.SendDate, EXPECT_MIN_GAP_DAYS)
      if (this.form.SendDate && val < min) {
        Toast.fail(`完成日期需晚于寄样日期 ${EXPECT_MIN_GAP_DAYS} 天（不早于 ${min}）`)
        return
      }
      this.form.ExpectedDate = val
      this.showExpectCalendar = false
    },
    /* ================= 样品 & 描述默认数据 ================= */
    /* 默认给三条样品描述：钢板号 / 炉批号 / 试样编号 */
    createEmptySample() {
      return {
        sampleName: '',
        descriptions: [{ category: '钢板号', content: '' }, { category: '炉批号', content: '' }, { category: '试样编号', content: '' }],
        mark: '',
        thickness: '',
        quantity: '',
        testItems: [
          {
            itemId: '',
            itemName: '',
            quantity: '',
            testStandard: '',
            testStandardId: '',
            judgmentStandard: '',
            remark: ''
          }
        ]
      }
    },
    async addSample() {
      this.blurActiveElement()
      this.form.Samples.push(this.createEmptySample())
      this.scrollToLastSample()
    },
    async getDicts() {
      let _that = this
      axios
        .get(window.config.apiUrl + '/api/Public/GetDictionary', {
          params: { types: '样品类型' },
          timeout: 10000
        })
        .then(function(response) {
          if (response.data.code == 1) {
            if (response.data.data != null) {
              _that.savedSampleNames = [...new Set(response.data.data.filter(item => item.Type === '样品类型').map(item => item.Name))]
            }
          } else {
            Toast(response.data.msg)
          }
        })
        .catch(function(error) {})
    },
    /* ================= 检测项目 / 试验依据标准 字典 ================= */
    async getTestDictionary() {
      const _that = this
      axios
        .get(window.config.apiUrl + '/api/Public/GetTestDictionary', {
          params: {
            companyId: _that.form.CompanyId
          },
          timeout: 10000
        })
        .then(function(response) {
          if (response.data && response.data.code == 1) {
            const list = Array.isArray(response.data.data) ? response.data.data : []
            _that.testDictionary = list

            const itemIdMap = {}
            const nameMap = {}
            const stdIdMap = {}

            list.forEach(item => {
              if (!item || !item.Name) return
              const itemName = item.Name
              if (!nameMap[itemName]) {
                nameMap[itemName] = []
                itemIdMap[itemName] = item.Id || ''
              }
              const methods = Array.isArray(item.Methods) ? item.Methods : []
              methods.forEach(m => {
                if (m && m.Name) {
                  nameMap[itemName].push(m.Name)
                  const key = itemName + '::' + m.Name
                  if (!stdIdMap[key] && m.Id) stdIdMap[key] = m.Id
                }
              })
            })
            Object.keys(nameMap).forEach(k => {
              nameMap[k] = Array.from(new Set(nameMap[k]))
            })

            _that.itemIdMap = itemIdMap
            _that.itemStandardMap = nameMap
            _that.standardIdMap = stdIdMap
            _that.presetTestItems = Object.keys(nameMap)

            console.log('_that.itemIdMap:', _that.itemIdMap)
            console.log('_that.itemStandardMap:', _that.itemStandardMap)
            console.log('_that.standardIdMap:', _that.standardIdMap)
            console.log('_that.presetTestItems:', _that.presetTestItems)
          } else {
            Toast(response.data && response.data.msg)
          }
        })
        .catch(function(error) {})
    },
    removeSample(idx) {
      if (this.form.Samples.length <= 1) return Toast('至少保留一条样品信息')
      this.blurActiveElement()
      Dialog.confirm({
        title: '删除样品',
        message: `确定删除「样品 ${idx + 1}」吗？删除后不可恢复。`,
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      })
        .then(() => {
          this.form.Samples.splice(idx, 1)
          Toast.success('已删除')
        })
        .catch(() => {})
    },
    reuseLastSample() {
      this.blurActiveElement()
      if (!this.form.Samples.length) return
      const last = this.form.Samples[this.form.Samples.length - 1]
      const copy = JSON.parse(JSON.stringify(last))
      if (!Array.isArray(copy.descriptions) || !copy.descriptions.length) {
        copy.descriptions = [
          { category: '钢板号', content: '' },
          { category: '炉批号', content: '' },
          { category: '试样编号', content: '' }
        ]
      }
      if (!Array.isArray(copy.testItems) || !copy.testItems.length)
        copy.testItems = [
          { itemId: '', itemName: '', quantity: '', testStandard: '', testStandardId: '', judgmentStandard: '', remark: '' }
        ]
      if (copy.mark == null) copy.mark = ''
      if (copy.thickness == null) copy.thickness = ''
      this.form.Samples.push(copy)
      this.scrollToLastSample()
    },
    scrollToLastSample() {
      this.$nextTick(() => {
        const items = document.querySelectorAll('.sample-item')
        if (items.length) items[items.length - 1].scrollIntoView({ block: 'start' })
      })
    },
    addDescription(sampleIdx) {
      if (this.isApproved) return
      const list = this.form.Samples[sampleIdx].descriptions
      if (!Array.isArray(list)) this.$set(this.form.Samples[sampleIdx], 'descriptions', [])
      this.form.Samples[sampleIdx].descriptions.push({ category: '', content: '' })
      this.$nextTick(() => {
        const allRows = document.querySelectorAll('.desc-row')
        if (allRows.length) allRows[allRows.length - 1].scrollIntoView({ block: 'center' })
      })
    },
    removeDescription(sampleIdx, di) {
      if (this.isApproved) return
      const list = this.form.Samples[sampleIdx].descriptions
      if (!Array.isArray(list) || list.length <= 1) return
      list.splice(di, 1)
    },
    buildDescriptionString(descs) {
      if (!Array.isArray(descs)) return ''
      return descs
        .map(d => {
          const c = (d && d.category ? String(d.category) : '').trim()
          const v = (d && d.content ? String(d.content) : '').trim()
          if (!c && !v) return ''
          return c ? `${c}：${v}` : v
        })
        .filter(Boolean)
        .join('；')
    },
    addTestItem(sampleIdx) {
      if (this.isApproved) return
      const list = this.form.Samples[sampleIdx].testItems
      if (!Array.isArray(list)) this.$set(this.form.Samples[sampleIdx], 'testItems', [])
      this.form.Samples[sampleIdx].testItems.push({
        itemId: '',
        itemName: '',
        quantity: '',
        testStandard: '',
        testStandardId: '',
        judgmentStandard: '',
        remark: ''
      })
      this.$nextTick(() => {
        const blocks = document.querySelectorAll('.test-item-block')
        if (blocks.length) blocks[blocks.length - 1].scrollIntoView({ block: 'center' })
      })
    },
    reuseTestItem(sampleIdx, itemIdx) {
      if (this.isApproved) return
      const sample = this.form.Samples[sampleIdx]
      if (!sample || !Array.isArray(sample.testItems)) return
      const source = sample.testItems[itemIdx]
      if (!source) return
      const copy = {
        itemId: source.itemId || '',
        itemName: source.itemName || '',
        quantity: source.quantity || '',
        testStandard: source.testStandard || '',
        testStandardId: source.testStandardId || '',
        judgmentStandard: source.judgmentStandard || '',
        remark: source.remark || ''
      }
      sample.testItems.splice(itemIdx + 1, 0, copy)
      this.$nextTick(() => {
        const sampleEl = document.querySelectorAll('.sample-item')[sampleIdx]
        if (sampleEl) {
          const items = sampleEl.querySelectorAll('.test-item-block')
          if (items[itemIdx + 1]) items[itemIdx + 1].scrollIntoView({ block: 'center' })
        }
      })
    },
    removeTestItem(sampleIdx, tiIdx) {
      if (this.isApproved) return
      const sample = this.form.Samples[sampleIdx]
      if (!sample || !Array.isArray(sample.testItems)) return
      const list = sample.testItems
      if (list.length <= 1) return Toast('至少保留一条检测项目')
      this.blurActiveElement()
      Dialog.confirm({
        title: '删除检测项目',
        message: `确定删除「检测项目 #${tiIdx + 1}」吗？删除后不可恢复。`,
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      })
        .then(() => {
          list.splice(tiIdx, 1)
          Toast.success('已删除')
        })
        .catch(() => {})
    },
    openTestItemNamePicker(sampleIdx, itemIdx) {
      if (this.isApproved) return
      this.blurActiveElement()
      this.testItemNamePicker.sampleIdx = sampleIdx
      this.testItemNamePicker.itemIdx = itemIdx
      this.testItemNamePicker.search = ''
      this.testItemNamePicker.customInput = ''
      this.testItemNamePicker.show = true
    },
    isCurrentTestItemName(item) {
      const { sampleIdx, itemIdx } = this.testItemNamePicker
      if (sampleIdx < 0 || itemIdx < 0) return false
      const sample = this.form.Samples[sampleIdx]
      if (!sample || !Array.isArray(sample.testItems)) return false
      const t = sample.testItems[itemIdx]
      return t && t.itemName === item
    },
    onTestItemNameSelect(name) {
      const { sampleIdx, itemIdx } = this.testItemNamePicker
      if (sampleIdx >= 0 && itemIdx >= 0) {
        const sample = this.form.Samples[sampleIdx]
        if (sample && Array.isArray(sample.testItems) && sample.testItems[itemIdx]) {
          const ti = sample.testItems[itemIdx]
          this.$set(ti, 'itemName', name)
          this.$set(ti, 'itemId', this.itemIdMap[name] || '')

          const prevStd = ti.testStandard
          if (prevStd) {
            const validList = this.itemStandardMap[name] || []
            if (validList.indexOf(prevStd) === -1) {
              this.$set(ti, 'testStandard', '')
              this.$set(ti, 'testStandardId', '')
            } else {
              const stdKey = name + '::' + prevStd
              this.$set(ti, 'testStandardId', this.standardIdMap[stdKey] || '')
            }
          }
        }
      }
      this.testItemNamePicker.show = false
    },
    addCustomTestItemName() {
      const val = this.testItemNamePicker.customInput.trim()
      if (!val) return
      if (!this.presetTestItems.includes(val)) this.presetTestItems.unshift(val)
      if (!this.itemIdMap[val]) this.$set(this.itemIdMap, val, '')
      if (!this.itemStandardMap[val]) this.$set(this.itemStandardMap, val, [])
      this.onTestItemNameSelect(val)
      this.testItemNamePicker.customInput = ''
      this.saveCustomDictItem('检测项目', val)
    },
    saveCustomDictItem(type, name) {
      if (!type || !name) return
      const postLoading = Toast.loading({ duration: 0, forbidClick: true, message: '加载中...' })
      axios
        .get(window.config.apiUrl + '/api/Public/addDictionary', {
          params: { type: type, name: name },
          timeout: 10000
        })
        .then(function(response) {
          postLoading.clear()
          if (response.data && response.data.code == 1) Toast.success('保存成功')
          else Toast.fail('保存失败')
        })
        .catch(function(error) {
          postLoading.clear()
        })
    },
    openTestStandardPicker(sampleIdx, itemIdx) {
      if (this.isApproved) return
      this.blurActiveElement()
      this.testStandardPicker.sampleIdx = sampleIdx
      this.testStandardPicker.itemIdx = itemIdx
      this.testStandardPicker.search = ''
      this.testStandardPicker.customInput = ''

      const sample = this.form.Samples[sampleIdx]
      const item = sample && Array.isArray(sample.testItems) ? sample.testItems[itemIdx] : null
      const itemName = item && item.itemName ? String(item.itemName).trim() : ''

      this.savedTestStandards = itemName && this.itemStandardMap[itemName] ? this.itemStandardMap[itemName].slice() : []

      this.testStandardPicker.show = true
    },
    isCurrentTestStandard(std) {
      const { sampleIdx, itemIdx } = this.testStandardPicker
      if (sampleIdx < 0 || itemIdx < 0) return false
      const sample = this.form.Samples[sampleIdx]
      if (!sample || !Array.isArray(sample.testItems)) return false
      const t = sample.testItems[itemIdx]
      return t && t.testStandard === std
    },
    isStandardOutOfScope(sampleIdx, itemIdx) {
      const sample = this.form.Samples[sampleIdx]
      if (!sample || !Array.isArray(sample.testItems)) return false
      const item = sample.testItems[itemIdx]
      if (!item) return false

      const std = String(item.testStandard || '').trim()
      if (!std) return false

      const itemName = String(item.itemName || '').trim()
      if (!itemName) return false

      const allowed = this.itemStandardMap[itemName]
      if (!Array.isArray(allowed)) return true

      return allowed.indexOf(std) === -1
    },
    onTestStandardSelect(std) {
      const { sampleIdx, itemIdx } = this.testStandardPicker
      if (sampleIdx >= 0 && itemIdx >= 0) {
        const sample = this.form.Samples[sampleIdx]
        if (sample && Array.isArray(sample.testItems) && sample.testItems[itemIdx]) {
          const ti = sample.testItems[itemIdx]
          const itemName = String(ti.itemName || '').trim()
          this.$set(ti, 'testStandard', std)
          const stdKey = itemName + '::' + std
          this.$set(ti, 'testStandardId', this.standardIdMap[stdKey] || '')
        }
      }
      this.testStandardPicker.show = false
    },
    addCustomTestStandard() {
      const val = this.testStandardPicker.customInput.trim()
      if (!val) return

      if (this.savedTestStandards.indexOf(val) === -1) this.savedTestStandards.unshift(val)

      const { sampleIdx, itemIdx } = this.testStandardPicker
      const sample = this.form.Samples[sampleIdx]
      const item = sample && Array.isArray(sample.testItems) ? sample.testItems[itemIdx] : null
      const nameKey = item && item.itemName ? String(item.itemName).trim() : ''
      if (nameKey) {
        if (!this.itemStandardMap[nameKey]) this.$set(this.itemStandardMap, nameKey, [])
        if (this.itemStandardMap[nameKey].indexOf(val) === -1) this.itemStandardMap[nameKey].unshift(val)
        const stdKey = nameKey + '::' + val
        if (!this.standardIdMap[stdKey]) this.$set(this.standardIdMap, stdKey, '')
      }

      this.onTestStandardSelect(val)
      this.testStandardPicker.customInput = ''
    },
    openSampleNamePicker(idx) {
      if (this.isApproved) return
      this.blurActiveElement()
      this.sampleNamePicker.currentSampleIndex = idx
      this.sampleNamePicker.search = ''
      this.sampleNamePicker.customInput = ''
      this.sampleNamePicker.show = true
    },
    isCurrentSampleName(name) {
      const idx = this.sampleNamePicker.currentSampleIndex
      if (idx < 0) return false
      const sample = this.form.Samples[idx]
      return sample && sample.sampleName === name
    },
    onSampleNameSelect(name) {
      const idx = this.sampleNamePicker.currentSampleIndex
      if (idx >= 0) this.form.Samples[idx].sampleName = name
      this.sampleNamePicker.show = false
    },
    addCustomSampleName() {
      const val = this.sampleNamePicker.customInput.trim()
      if (!val) return
      if (!this.savedSampleNames.includes(val)) this.savedSampleNames.unshift(val)
      this.onSampleNameSelect(val)
      this.sampleNamePicker.customInput = ''
      // this.saveCustomDictItem('样品类型', val)
    },
    rememberSampleName(name) {
      if (this.isApproved) return
      const val = (name || '').trim()
      if (val && !this.savedSampleNames.includes(val)) this.savedSampleNames.unshift(val)
    },
    handleAttachment(e) {
      if (this.isApproved) return
      const file = e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!this.isAllowedAttachment(file.name)) {
        Toast.fail('仅支持上传 Excel（.xls / .xlsx）或 Word（.doc / .docx）文件')
        return
      }
      const MAX_SIZE = 5 * 1024 * 1024
      if (file.size > MAX_SIZE) {
        Toast.fail('附件不能超过 5MB')
        return
      }
      this.form.AttachmentFile = file
    },
    viewAttachment() {
      const f = this.form || {}
      let opened = false

      if (f.AttachmentFile && typeof f.AttachmentFile === 'object') {
        try {
          const url = URL.createObjectURL(f.AttachmentFile)
          const w = window.open(url, '_blank')
          if (w) opened = true
          setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000)
        } catch (e) {}
      }

      if (!opened) {
        const url = this.attachmentUrl
        if (url) {
          try {
            const w = window.open(url, '_blank')
            if (w) opened = true
          } catch (e) {}
          if (!opened) {
            Toast.fail('附件打开失败，请检查浏览器是否拦截了新窗口')
          }
        } else {
          Toast('暂无附件可查看')
        }
      }
    },
    downAttachmentTemplate() {
      let downloadUrl = window.config.apiUrl + '/upload/template/委托单B-4.doc'
      window.open(downloadUrl, '_blank')
    },
    validateForm() {
      if (this.isApproved) return true
      const f = this.form

      if (!f.CompanyName || !f.CompanyName.trim()) {
        return Toast.fail('请填写委托单位'), false
      }

      if (!f.SendDate) {
        return Toast.fail('请选择寄样日期'), false
      }

      if (f.ExpectedDate) {
        const minExpected = this.addDays(f.SendDate, EXPECT_MIN_GAP_DAYS)
        if (f.ExpectedDate < minExpected) {
          return Toast.fail(`完成日期需晚于寄样日期 ${EXPECT_MIN_GAP_DAYS} 天（不早于 ${minExpected}）`), false
        }
      }

      if (!f.ProjectAddress || !f.ProjectAddress.trim()) {
        return Toast.fail('请填写委托地址'), false
      }

      if (!f.OutputLanguage) {
        return Toast.fail('请选择报告输出语言'), false
      }

      if (!f.ContactPhone || !f.ContactPhone.trim()) {
        return Toast.fail('请填写联系人手机号'), false
      }

      if (!f.SampleInputMethod) {
        return Toast.fail('请选择样品信息录入方式'), false
      }

      if (f.SampleInputMethod === '2') {
        if (!f.AttachmentFile) {
          return Toast.fail('请上传样品附件'), false
        }
        if (!this.isAllowedAttachment(f.AttachmentFile.name)) {
          return Toast.fail('样品附件仅支持 Excel（.xls / .xlsx）或 Word（.doc / .docx）文件'), false
        }
      } else {
        if (!f.Samples.length) {
          return Toast.fail('请至少添加一条样品信息'), false
        }
        for (let i = 0; i < f.Samples.length; i++) {
          const s = f.Samples[i]

          if (!s.sampleName || !s.sampleName.trim()) {
            return Toast.fail(`样品 #${i + 1} 请填写试样名称`), false
          }

          if (!s.mark || !String(s.mark).trim()) {
            return Toast.fail(`样品 #${i + 1} 请填写牌号`), false
          }
          console.log('opopop:', s.thickness)
          if (!s.thickness || !String(s.thickness).trim()) {
            return Toast.fail(`样品 #${i + 1} 请填写厚度`), false
          }

          if (!s.quantity || !/^\d+$/.test(String(s.quantity)) || Number(s.quantity) < 1) {
            return Toast.fail(`样品 #${i + 1} 请填写有效的来样数量（正整数）`), false
          }

          const items = Array.isArray(s.testItems) ? s.testItems : []
          if (!items.length) {
            return Toast.fail(`样品 #${i + 1} 请至少添加一条检测项目`), false
          }

          for (let j = 0; j < items.length; j++) {
            const it = items[j]
            if (!it.itemName || !String(it.itemName).trim()) {
              return Toast.fail(`样品 #${i + 1} 检测项目 #${j + 1} 请填写检测项目名称`), false
            }
            if (!it.quantity || !/^\d+$/.test(String(it.quantity)) || Number(it.quantity) < 1) {
              return Toast.fail(`样品 #${i + 1} 检测项目 #${j + 1} 请填写有效的检测数量（正整数）`), false
            }
            if (!it.judgmentStandard || !String(it.judgmentStandard).trim()) {
              return Toast.fail(`样品 #${i + 1} 检测项目 #${j + 1} 请填写判定标准`), false
            }
          }
        }
      }

      return true
    },

    isOrderNoEmpty() {
      const v = this.form.OrderNo
      return v === null || v === undefined || String(v).trim() === ''
    },
    isEmpty(param) {
      return param === null || param === undefined || String(param).trim() === ''
    },
    async saveDraft(isSubmit) {
      if (this.isApproved) return
      this.blurActiveElement()
      const _that = this

      if (this.isOrderNoEmpty()) {
        return Toast.fail('委托编号未生成，不可保存'), false
      }

      const postLoading = Toast.loading({ duration: 0, forbidClick: true, message: isSubmit ? '提交中...' : '暂存中...' })
      try {
        const reqModel = { ...this.form }
        reqModel.IsSubmit = isSubmit
        reqModel.SubmitFrom = 1 //客户
        if (!this.isEmpty(reqModel.SendDate)) {
          reqModel.SendDate = moment(reqModel.SendDate).format('YYYY-MM-DD')
        }
        if (!this.isEmpty(reqModel.ExpectedDate)) {
          reqModel.ExpectedDate = moment(reqModel.ExpectedDate).format('YYYY-MM-DD')
        }
        if (Array.isArray(this.form.Samples)) {
          reqModel.Samples = JSON.parse(JSON.stringify(this.form.Samples)).map(s => {
            s.description = this.buildDescriptionString(s.descriptions)
            s.mark = s.mark != null ? s.mark : ''
            s.thickness = s.thickness != null ? s.thickness : ''
            s.quantity = s.quantity != null ? String(s.quantity).replace(/\D/g, '') : ''
            if (Array.isArray(s.testItems)) {
              s.testItems = s.testItems.map(t => {
                if (!t) return t
                if (t.quantity != null) t.quantity = String(t.quantity).replace(/\D/g, '')
                t.itemId = t.itemId != null ? t.itemId : ''
                t.testStandardId = t.testStandardId != null ? t.testStandardId : ''
                return t
              })
            }
            return s
          })
        }
        if (this.form.AttachmentFile) {
          const file = this.form.AttachmentFile
          const base64 = await this.fileToBase64(file)
          const ext = this.getFileExt(file.name)
          const fileName = this.getFileName(file.name)
          reqModel.AttachmentFileName = fileName
          reqModel.AttachmentFileType = ext
          reqModel.AttachmentFileBase64 = base64
          reqModel.AttachmentFile = null
        } else {
          reqModel.AttachmentFile = null
          reqModel.AttachmentFileBase64 = ''
          reqModel.AttachmentFileType = ''
          reqModel.AttachmentFileName = ''
        }
        const sig = this.form.SignatureData
        if (sig) {
          if (String(sig).startsWith('data:')) {
            const { base64 } = this.dataUrlToBase64(sig)
            reqModel.SignatureBase64 = base64
            reqModel.SignatureData = null
            reqModel.SignatureFilePath = null
          } else {
            reqModel.SignatureFilePath = this.form.SignatureFilePath || ''
            reqModel.SignatureData = null
            reqModel.SignatureBase64 = ''
          }
        } else {
          reqModel.SignatureData = null
          reqModel.SignatureBase64 = ''
          reqModel.SignatureFilePath = ''
        }
        const response = await axios.post(window.config.apiUrl + '/api/Public/AddOrEditCustomerCommissionOrder', reqModel)
        postLoading.clear()
        if (response.data.code == 1) {
          _that.form.Id = response.data.data
          Toast.success(isSubmit ? '委托单已提交' : '已为您保存数据')
          if (isSubmit) {
            this.$router.go(-1)
          }
        } else {
          Toast(response.data.msg)
        }
      } catch (error) {
        postLoading.clear()
        Toast('网络错误')
      }
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const result = reader.result || ''
          const base64 = String(result).split(',')[1] || ''
          resolve(base64)
        }
        reader.onerror = e => reject(e)
        reader.readAsDataURL(file)
      })
    },
    fileToDataURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result || '')
        reader.onerror = e => reject(e)
        reader.readAsDataURL(file)
      })
    },
    dataUrlToBase64(dataUrl) {
      const str = String(dataUrl || '')
      const match = str.match(/^data:([^;]+);base64,(.*)$/)
      if (match) return { type: match[1], base64: match[2] }
      return { type: 'image/png', base64: str }
    },
    initInlineSignature() {
      const canvas = this.$refs.signatureCanvas
      if (!canvas) return
      const w = canvas.offsetWidth
      const h = canvas.offsetHeight
      if (!w || !h) return
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
      const ctx = canvas.getContext('2d')
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = '#000000' // ← 原来是 '#1f2a3e'，改为纯黑
      this.signCtx = ctx
      const sig = this.form.SignatureData
      if (sig && String(sig).startsWith('data:')) {
        const img = new Image()
        img.onload = () => {
          ctx.drawImage(img, 0, 0, w, h)
        }
        img.src = sig
      }
    },
    onWindowResize() {
      setTimeout(() => {
        this.initInlineSignature()
      }, 300)
    },
    getPos(e) {
      const canvas = this.$refs.signatureCanvas
      if (!canvas) return { x: 0, y: 0 }
      const rect = canvas.getBoundingClientRect()
      let sx, sy
      if (e.touches && e.touches.length) {
        sx = e.touches[0].clientX
        sy = e.touches[0].clientY
      } else {
        sx = e.clientX
        sy = e.clientY
      }
      return { x: sx - rect.left, y: sy - rect.top }
    },
    startSign(e) {
      if (this.isApproved) return
      if (this.form.SignatureData && !String(this.form.SignatureData).startsWith('data:')) {
        Toast('如需重签，请先点击「重新签字」')
        return
      }
      if (!this.signCtx) return
      this.isSigning = true
      const pos = this.getPos(e)
      this.signLast = pos
      this.signCtx.beginPath()
      this.signCtx.arc(pos.x, pos.y, 1.5, 0, Math.PI * 2)
      this.signCtx.fillStyle = '#000000' // ← 原来是 '#1f2a3e'，改为纯黑
      this.signCtx.fill()
    },
    moveSign(e) {
      if (this.isApproved) return
      if (!this.isSigning || !this.signCtx) return
      const pos = this.getPos(e)
      this.signCtx.beginPath()
      this.signCtx.moveTo(this.signLast.x, this.signLast.y)
      this.signCtx.lineTo(pos.x, pos.y)
      this.signCtx.stroke()
      this.signLast = pos
    },
    endSign() {
      if (this.isApproved) return
      if (!this.isSigning) return
      this.isSigning = false
      const canvas = this.$refs.signatureCanvas
      if (canvas) {
        this.form.SignatureData = canvas.toDataURL('image/png')
        this.form.SignatureFilePath = ''
        if (!this.form.SignatureDate) this.form.SignatureDate = this.formatDate(new Date())
      }
    },
    reSign() {
      Dialog.confirm({
        title: '重新签字',
        message: '重新签字将清除当前签名，确定继续？'
      })
        .then(() => {
          this.clearSignature()
          Toast('已清除，请重新签字')
        })
        .catch(() => {})
    },
    submitForm() {
      if (!this.validateForm()) return
      if (!this.form.SignatureData) return Toast.fail('请委托方手写签名')
      this.saveDraft(true)
    }
  }
}
</script>

<style scoped>
.app-container {
  --metal-bg: #f4f6fa;
  --metal-surface: rgba(255, 255, 255, 0.92);
  --metal-border: rgba(0, 0, 0, 0.08);
  --metal-glow: 0 8px 20px -8px rgba(0, 0, 0, 0.1);
  --accent: #2c7be5;
  --accent-light: #5d9eff;
  --text-primary: #1e2a3e;
  --text-secondary: #5c6f87;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  background: linear-gradient(145deg, #eef2f6 0%, #d9e0e8 100%);
  min-height: 100vh;
  box-sizing: border-box;
  overscroll-behavior-y: contain;
}

.nav-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}
.nav-left {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 500;
  color: var(--accent);
  cursor: pointer;
  transition: opacity 0.2s;
}
.nav-left:active {
  opacity: 0.6;
}
.back-icon {
  font-size: 20px;
}
.nav-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.3px;
}
.nav-right {
  width: 50px;
}

.card {
  margin-top: 10px;
  background: #fff;
  border-radius: 16px;
  padding: 4px 16px 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
  margin-left: 16px;
  margin-right: 16px;
  position: relative;
}
.card:last-child {
  margin-bottom: 0;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 0 14px;
  border-bottom: 1px solid #eef2f6;
  color: #1f2a3e;
}
.card-title .title-icon {
  font-size: 20px;
  color: #3f8cff;
}
.card-title .del-btn {
  margin-left: auto;
  font-size: 13px;
  color: #ee0a24;
  cursor: pointer;
  padding: 2px 6px;
}

.shipping-card-highlight {
  border: 2px solid #07c160;
  border-radius: 16px;
  animation: shipping-blink 1.2s ease-in-out infinite;
  box-sizing: border-box;
}
@keyframes shipping-blink {
  0% {
    border-color: #07c160;
    box-shadow: 0 0 0 0 rgba(7, 193, 96, 0.55), 0 0 12px 2px rgba(7, 193, 96, 0.35);
  }
  50% {
    border-color: #28d47f;
    box-shadow: 0 0 0 8px rgba(7, 193, 96, 0), 0 0 22px 6px rgba(7, 193, 96, 0.85);
  }
  100% {
    border-color: #07c160;
    box-shadow: 0 0 0 0 rgba(7, 193, 96, 0.55), 0 0 12px 2px rgba(7, 193, 96, 0.35);
  }
}

::v-deep .van-cell {
  padding: 14px 0;
  font-size: 14px;
  line-height: 24px;
  align-items: center;
}
::v-deep .van-cell::after {
  left: 0;
  right: 0;
  border-bottom-color: #f2f3f5;
}
::v-deep .van-cell:last-child::after {
  display: none;
}
::v-deep .van-field__label {
  color: #1f2a3e;
  font-weight: 500;
}
::v-deep .van-field__control {
  color: #1f2a3e;
  text-align: right;
}
::v-deep .van-field__control::placeholder {
  color: #c8c9cc;
}
::v-deep .van-field--readonly .van-field__control {
  color: #1f2a3e;
  -webkit-text-fill-color: #1f2a3e;
  opacity: 1;
}
::v-deep .van-field--readonly .van-field__control::placeholder {
  color: #c8c9cc;
  -webkit-text-fill-color: #c8c9cc;
}
::v-deep .van-field__control[readonly],
::v-deep .van-field__control[disabled] {
  pointer-events: none;
}
::v-deep .van-radio__control,
::v-deep .van-checkbox__control {
  pointer-events: none;
}

.cell-tip {
  display: inline-block;
  margin-top: 2px;
  font-size: 12px;
  line-height: 16px;
  color: #9ca3af;
}
.required-mark {
  color: #ee0a24;
  margin-left: 2px;
}

.radio-inline {
  display: flex;
  justify-content: flex-end;
}
.radio-inline ::v-deep .van-radio {
  margin-right: 18px;
}
.radio-inline ::v-deep .van-radio:last-child {
  margin-right: 0;
}
.radio-inline ::v-deep .van-radio__label {
  font-size: 13px;
  color: #1f2a3e;
  margin-left: 4px;
}

.sample-process-radio {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 0;
  width: 100%;
}
.sample-process-radio ::v-deep .van-radio {
  margin-right: 0 !important;
  display: flex;
  align-items: center;
}
.sample-process-radio ::v-deep .van-radio__label {
  font-size: 13px;
  color: #1f2a3e;
  margin-left: 4px;
  white-space: nowrap;
}

.sample-name-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0 2px;
}
.sample-name-tag {
  display: inline-block;
  font-size: 13px;
  line-height: 1.4;
  color: #3f8cff;
  background: rgba(63, 140, 255, 0.08);
  border: 1px solid rgba(63, 140, 255, 0.35);
  border-radius: 14px;
  padding: 4px 12px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}
.sample-name-tag:active {
  opacity: 0.6;
}
.sample-name-tag.active {
  color: #fff;
  background: #3f8cff;
  border-color: #3f8cff;
  box-shadow: 0 2px 8px rgba(63, 140, 255, 0.35);
}

.inline-field {
  padding: 14px 0 !important;
  font-size: 14px;
  align-items: center;
}
.inline-field::after {
  left: 0;
  right: 0;
  border-bottom-color: #f2f3f5;
}
.inline-field ::v-deep .van-field__control {
  text-align: right;
  color: #1f2a3e;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.inline-field ::v-deep .van-field__control::placeholder {
  color: #c8c9cc;
}
.inline-field ::v-deep .van-field__extra {
  color: #7b8594;
  font-size: 13px;
  padding-left: 6px;
}
.unit-text {
  font-size: 13px;
  color: #7b8594;
}
.dropdown-icon {
  font-size: 14px;
  color: #c8c9cc;
  padding-left: 6px;
}

.field-block {
  padding: 12px 0;
  border-bottom: 1px solid #f2f3f5;
}
.field-block:last-child {
  border-bottom: none;
}
.field-label {
  font-size: 13px;
  color: #7b8594;
  margin-bottom: 4px;
}
.field-block ::v-deep .van-cell {
  padding: 0;
  font-size: 15px;
  line-height: 24px;
  background: transparent;
}
.field-block ::v-deep .van-cell::after {
  display: none;
}
.field-block ::v-deep .van-field__control {
  text-align: left;
  font-size: 15px;
  color: #1f2a3e;
}
.field-block ::v-deep .van-field__control::placeholder {
  color: #c8c9cc;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.field-label-row .field-label {
  margin-bottom: 0;
  color: #5c6f87;
  font-weight: 500;
}
.add-desc-btn {
  font-size: 13px;
  color: #3f8cff;
  cursor: pointer;
  padding: 4px 8px;
  user-select: none;
  border-radius: 6px;
  background: rgba(63, 140, 255, 0.08);
  line-height: 1;
}
.add-desc-btn:active {
  opacity: 0.6;
}

.desc-input {
  box-sizing: border-box;
  height: 42px;
  padding: 0 12px;
  font-size: 15px;
  color: #1f2a3e;
  background: #ffffff;
  border: 1.5px solid #d5dce6;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  -webkit-appearance: none;
  box-shadow: 0 1px 2px rgba(31, 42, 62, 0.04);
}
.desc-input::placeholder {
  color: #a3adbd;
  font-size: 14px;
}
.desc-input:focus {
  border-color: #3f8cff;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(63, 140, 255, 0.14);
}
.desc-input[readonly] {
  background: #f2f3f5;
  border-color: #e6ebf1;
  color: #1f2a3e;
  pointer-events: none;
  box-shadow: none;
}
.desc-input[readonly]::placeholder {
  color: #b8bfcb;
}
.desc-input.desc-input-select {
  background: #ffffff;
  border-color: #d5dce6;
  color: #1f2a3e;
  cursor: pointer;
  caret-color: transparent;
  pointer-events: auto;
  box-shadow: 0 1px 2px rgba(31, 42, 62, 0.04);
}
.desc-input.desc-input-select::placeholder {
  color: #a3adbd;
}
.desc-input.desc-input-select:active {
  border-color: #3f8cff;
}

.desc-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.desc-row:last-child {
  margin-bottom: 0;
}
.desc-category {
  width: 92px;
  flex-shrink: 0;
  text-align: left;
}
.desc-content {
  flex: 1;
  min-width: 0;
}
.desc-sep {
  color: #b8bfcb;
  font-size: 15px;
  flex-shrink: 0;
  font-weight: 600;
}
.desc-del {
  flex-shrink: 0;
  font-size: 18px;
  color: #ee0a24;
  padding: 6px;
  cursor: pointer;
  border-radius: 50%;
  background: rgba(238, 10, 36, 0.06);
}
.desc-del:active {
  opacity: 0.6;
}

.spec-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.spec-mark {
  flex: 1 1 0;
  min-width: 0;
}
.spec-thickness-box {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
}
.spec-thickness-box .spec-thickness {
  width: 100%;
  padding-right: 46px;
}
.spec-thickness-box .unit-text {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: #7b8594;
  pointer-events: none;
}

.test-item-block {
  padding: 12px 10px 14px;
  background: #f7f8fa;
  border-radius: 12px;
  margin-bottom: 10px;
  position: relative;
}
.test-item-block:last-child {
  margin-bottom: 0;
}
.test-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  min-height: 24px;
}
.reuse-item-btn {
  font-size: 12px;
  color: #07c160;
  background: rgba(7, 193, 96, 0.1);
  padding: 3px 10px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  line-height: 1.4;
}
.reuse-item-btn:active {
  opacity: 0.6;
}
.test-item-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.test-item-row:last-child {
  margin-bottom: 0;
}
.test-item-label {
  flex-shrink: 0;
  width: 78px;
  font-size: 13px;
  color: #5c6f87;
  font-weight: 500;
  text-align: right;
  line-height: 1.2;
  white-space: nowrap;
}

/* ================= 标签单独一行（检测项目/试验依据标准/判定标准/备注） ================= */
.test-item-row-stacked {
  display: block;
}
.test-item-label-stacked {
  width: auto;
  text-align: left;
  margin-bottom: 6px;
  white-space: normal;
}
.test-item-row-stacked > .desc-input {
  width: 100%;
  display: block;
}
.test-item-row-stacked .input-with-arrow .desc-input {
  width: 100%;
}

.input-with-arrow {
  position: relative;
  flex: 1;
  min-width: 0;
}
.input-with-arrow .desc-input {
  width: 100%;
  padding-right: 34px;
}
.test-item-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a3adbd;
  font-size: 14px;
  cursor: pointer;
  pointer-events: auto;
}
.test-item-arrow:active {
  opacity: 0.6;
}
.test-item-qty {
  flex: 1;
  min-width: 0;
}
.test-item-del {
  margin-left: 0;
}

/* ================= 试验依据标准 CMA/CNAS 绿色高亮 ================= */
/* 当输入值含 CMA/CNAS 时，input 自身文字透明，仅保留光标；
   下方 .standard-highlight-mirror 镜像层负责显示带绿色高亮的文本。 */
.standard-input-hidden-text {
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
  caret-color: #1f2a3e;
}
.standard-highlight-mirror {
  position: absolute;
  left: 12px;
  right: 34px;
  top: 0;
  height: 42px;
  line-height: 42px;
  pointer-events: none;
  font-size: 15px;
  color: #1f2a3e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 2;
}
.standard-highlight-mirror .cma-green {
  color: #07c160;
  font-weight: 600;
}

/* ================= 试验依据标准不在认可范围提示 ================= */
.standard-warning {
  margin-top: 6px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #ee0a24;
  background: rgba(238, 10, 36, 0.06);
  border-left: 3px solid #ee0a24;
  border-radius: 6px;
  word-break: break-all;
}

.sign-card {
  padding-bottom: 16px;
}
.terms-check {
  margin: 12px 0;
  padding: 12px;
  background: #f7f8fa;
  border: 1.5px dashed #d5dce6;
  border-radius: 10px;
  transition: background 0.2s, border-color 0.2s;
}
.terms-check--active {
  background: rgba(63, 140, 255, 0.06);
  border-color: #3f8cff;
}
.terms-check ::v-deep .van-checkbox {
  align-items: flex-start;
}
.terms-check ::v-deep .van-checkbox__icon {
  margin-top: 2px;
}
.terms-check ::v-deep .van-checkbox__label {
  color: #5c6f87;
  font-size: 13px;
  line-height: 1.6;
  white-space: normal;
}
.terms-text {
  display: inline-block;
  text-align: left;
}

.sign-section {
  position: relative;
  margin: 12px 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1.5px dashed #d5dce6;
  background: #fafbfd;
  transition: opacity 0.2s;
}
.sign-section--disabled {
  opacity: 0.55;
  filter: grayscale(0.2);
}
.sign-section--locked {
  pointer-events: none;
  cursor: default;
}
.sign-section--locked .sign-canvas-inline {
  cursor: default;
  touch-action: auto;
}
.sign-canvas-inline {
  display: block;
  width: 100%;
  height: 200px;
  touch-action: none;
  cursor: crosshair;
  background: transparent;
}
.sign-image-inline {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fafbfd;
  z-index: 2;
  pointer-events: none;
}
.sign-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #b8bfcb;
  font-size: 16px;
  pointer-events: none;
  user-select: none;
  z-index: 1;
}
.sign-mask-tip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #ee0a24;
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;
  user-select: none;
  z-index: 3;
  text-align: center;
  white-space: nowrap;
}
.sign-signed-badge {
  position: absolute;
  top: 8px;
  right: 10px;
  font-size: 12px;
  color: #07c160;
  background: rgba(7, 193, 96, 0.12);
  border-radius: 20px;
  padding: 3px 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  pointer-events: none;
  z-index: 3;
}
.sign-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
}
.sign-date {
  font-size: 13px;
  color: #7b8594;
  white-space: nowrap;
}
.sign-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}
.sign-actions ::v-deep .van-button {
  border-radius: 8px;
  padding: 0 16px;
  height: 36px;
  font-size: 13px;
  white-space: nowrap;
}

.sample-section-card {
  padding-bottom: 16px;
}
.method-field {
  padding: 12px 0 12px !important;
}
.method-field::after {
  left: 0;
  right: 0;
  border-bottom-color: #eef2f6;
}
.method-radio {
  display: flex;
  justify-content: flex-start;
}
.method-radio ::v-deep .van-radio {
  margin-right: 24px;
}
.method-radio ::v-deep .van-radio:last-child {
  margin-right: 0;
}
.method-radio ::v-deep .van-radio__label {
  font-size: 14px;
  color: #1f2a3e;
  margin-left: 4px;
}

.attach-upload {
  padding: 14px 0 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.attach-upload .file-name {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #3f8cff;
  background: rgba(63, 140, 255, 0.08);
  padding: 4px 10px;
  border-radius: 8px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attach-upload .remove-file {
  color: #ee0a24;
  cursor: pointer;
  margin-left: 4px;
  font-weight: 700;
}
.attach-tip {
  font-size: 12px;
  color: #9ca3af;
}
.attach-upload ::v-deep .van-button--plain {
  border-radius: 8px;
  height: 30px;
  padding: 0 10px;
  font-size: 13px;
}

.sample-item {
  padding: 4px 0;
  border-bottom: 1px solid #eef2f6;
}
.sample-item:last-of-type {
  border-bottom: none;
}
.sample-item-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 0 4px;
}
.sample-item-index {
  font-size: 14px;
  font-weight: 600;
  color: #3f8cff;
  position: relative;
  padding-left: 12px;
}
.sample-item-index::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 14px;
  border-radius: 2px;
  background: #3f8cff;
}
.sample-item-title .del-btn {
  margin-left: auto;
  font-size: 13px;
  color: #ee0a24;
  cursor: pointer;
  padding: 2px 6px;
}

.sample-buttons {
  display: flex;
  gap: 12px;
  margin-top: 14px;
}
.sample-buttons ::v-deep .van-button {
  flex: 1;
  border-radius: 12px;
  height: 46px;
  font-size: 15px;
}

.remark-textarea ::v-deep .van-cell {
  padding: 4px 0 12px;
}
.remark-textarea ::v-deep .van-field__control {
  text-align: left;
  background: #f7f8fa;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 22px;
}

.submit-btn-wrap {
  margin-top: 20px;
  display: flex;
  gap: 12px;
  padding: 0 16px;
  padding-bottom: 24px;
}
.submit-btn-wrap ::v-deep .van-button {
  flex: 1;
  height: 50px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  box-sizing: border-box;
}
.submit-btn-wrap .draft-btn {
  background: #fff;
  border: 1px solid #3f8cff;
  color: #3f8cff;
  box-shadow: none;
}
.submit-btn-wrap .submit-btn {
  background: linear-gradient(135deg, #3f8cff, #5e6ad2);
  border: none;
  color: #fff;
  box-shadow: 0 6px 16px rgba(63, 140, 255, 0.35);
}

.popup-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 14px;
}
.popup-title .van-icon {
  color: #3f8cff;
}
.popup-actions {
  display: flex;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f2f6;
}
.popup-actions ::v-deep .van-button {
  border-radius: 10px;
  height: 44px;
}
.empty-tip {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 20px 0;
}

.test-popup {
  border-radius: 20px 20px 0 0;
  padding: 20px 16px 20px;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}
.popup-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.popup-custom-input {
  flex: 1;
  box-sizing: border-box;
  padding: 8px 12px;
  background: #f7f8fa;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  color: #1f2a3e;
}
.popup-custom-input::placeholder {
  color: #c8c9cc;
}
.popup-search {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  background: #f2f3f5;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  margin-bottom: 12px;
  color: #1f2a3e;
}
.popup-search::placeholder {
  color: #c8c9cc;
}
.test-options-scroll {
  flex: 1;
  overflow-y: auto;
  max-height: 40vh;
}
.test-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 8px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}
.test-option:active {
  background: #f5f7fa;
}
.test-option .check {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid #c8c9cc;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.test-option .check.done {
  background: #3f8cff;
  border-color: #3f8cff;
}
.test-option .check.done::after {
  content: '✓';
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.name-popup {
  border-radius: 20px 20px 0 0;
  padding: 20px 16px 20px;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
}
.name-options-scroll {
  flex: 1;
  overflow-y: auto;
  max-height: 50vh;
}
.name-option {
  padding: 14px 12px;
  font-size: 15px;
  color: #1f2a3e;
  border-bottom: 1px solid #f2f3f5;
  cursor: pointer;
}
.name-option:last-child {
  border-bottom: none;
}
.name-option:active {
  background: #f5f7fa;
}
/* 委托编号 placeholder 红色 */
::v-deep .order-no-field .van-field__control::placeholder {
  color: #ee0a24;
  -webkit-text-fill-color: #ee0a24;
  opacity: 1;
}
</style>
